import * as React from "react";
import {
  addDays,
  endOfMonth,
  format,
  isSameDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";
import { CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";
import { cn } from "@aichat/ui/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Typography } from "./typography";
import { Separator } from "./separator";

const today = new Date();
const customDates = [
  {
    label: "Last 7 Days",
    value: {
      from: subDays(today, 7),
      to: today,
    },
  },
  {
    label: "Last 30 Days",
    value: {
      from: subDays(today, 30),
      to: today,
    },
  },
  {
    label: "This Week",
    value: {
      from: startOfWeek(today),
      to: today,
    },
  },
  {
    label: "This Month",
    value: {
      from: startOfMonth(today),
      to: today,
    },
  },
  {
    label: "Last Month",
    value: {
      from: startOfMonth(subMonths(today, 1)),
      to: endOfMonth(subMonths(today, 1)),
    },
  },
  {
    label: "Last 3 Months",
    value: {
      from: subMonths(today, 3),
      to: today,
    },
  },
  {
    label: "Last 6 Months",
    value: {
      from: subMonths(today, 6),
      to: today,
    },
  },
  {
    label: "Custom Range",
    value: "custom",
  },
];

export type DateRangeType = DateRange | undefined;

type DatePickerWithRangeProps = {
  value: DateRangeType;
  onApply: (date: DateRangeType) => void;
} & React.HTMLAttributes<HTMLDivElement>;
export function DatePickerWithRange({
  className,
  value,
  onApply,
}: DatePickerWithRangeProps) {
  const [date, setDate] = React.useState<DateRangeType>({
    from: new Date(),
    to: addDays(new Date(), 20),
  });

  React.useEffect(() => {
    setDate(value);
  }, [value]);

  const [open, setOpen] = React.useState(false);

  const reset = () => {
    setDate(value);
  };

  const hasCustomDate = customDates
    .slice(0, -1)
    .some(
      (c) =>
        typeof c.value !== "string" &&
        date?.from &&
        date?.to &&
        isSameDay(c.value?.from, date?.from) &&
        isSameDay(c.value?.to, date?.to),
    );

  const handleApply = () => {
    onApply(date);
    setOpen(false);
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-max-[300px] justify-start text-left font-normal",
              !date && "text-muted-foreground",
            )}
          >
            <CalendarIcon />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex gap-4 p-4">
            <div className="flex flex-col gap-4 w-32">
              {customDates.map((customDate, idx) => {
                const isSelected =
                  typeof customDate.value === "string"
                    ? false
                    : getCustomSelected(customDate.value, date);
                const isCustom =
                  !hasCustomDate && customDate.value === "custom";
                return (
                  <Button
                    onClick={() => {
                      if (typeof customDate.value === "string") return;
                      setDate(customDate.value);
                    }}
                    variant="outline"
                    size="sm"
                    className={cn("text-muted-foreground", {
                      "border border-foreground text-foreground":
                        isSelected || isCustom,
                    })}
                    key={idx}
                  >
                    {customDate.label}
                  </Button>
                );
              })}
            </div>
            <div>
              <div className="flex gap-2 items-center mb-2">
                <Button variant="outline" className="gap-1" size="sm">
                  {date?.from ? format(date?.from, "dd") : ""}
                  <Typography className="text-gray-200">/</Typography>
                  {date?.from ? format(date?.from, "MM") : ""}
                  <Typography className="text-gray-200">/</Typography>
                  {date?.from ? format(date?.from, "yyyy") : ""}
                </Button>
                <Typography>-</Typography>
                <Button variant="outline" className="gap-1" size="sm">
                  {date?.to ? format(date?.to, "dd") : ""}
                  <Typography className="text-gray-200">/</Typography>
                  {date?.to ? format(date?.to, "MM") : ""}
                  <Typography className="text-gray-200">/</Typography>
                  {date?.to ? format(date?.to, "yyyy") : ""}
                </Button>
              </div>
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={date?.from}
                selected={date}
                onSelect={setDate}
                numberOfMonths={2}
              />
            </div>
          </div>
          <Separator />
          <div className="text-right flex justify-between p-4">
            <Button variant="ghost" onClick={reset}>
              Reset
            </Button>
            <Button onClick={handleApply}>Apply</Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

const getCustomSelected = (
  customDate: DateRange,
  date: DateRange | undefined,
) => {
  if (!date?.from || !date?.to || !customDate.from || !customDate.to)
    return false;
  const isFromSame = isSameDay(date.from, customDate.from);
  const isToSame = isSameDay(date.to, customDate.to);
  return isFromSame && isToSame;
};
